import React, { useState, useRef, useCallback, useEffect } from 'react';

const FILTERS = [
  { id: 'normal', label: 'Normal', css: 'none' },
  { id: 'clarendon', label: 'Clarendon', css: 'contrast(1.2) saturate(1.35)' },
  { id: 'gingham', label: 'Gingham', css: 'brightness(1.05) hue-rotate(-10deg) saturate(0.7)' },
  { id: 'moon', label: 'Moon', css: 'grayscale(1) contrast(1.1) brightness(1.1)' },
  { id: 'lark', label: 'Lark', css: 'contrast(0.9) brightness(1.15) saturate(0.85)' },
  { id: 'reyes', label: 'Reyes', css: 'sepia(0.22) brightness(1.1) contrast(0.85) saturate(0.75)' },
  { id: 'juno', label: 'Juno', css: 'contrast(1.15) saturate(1.8) sepia(0.08) brightness(1.05)' },
  { id: 'slumber', label: 'Slumber', css: 'saturate(0.66) brightness(1.05) contrast(0.88) sepia(0.15)' },
  { id: 'aden', label: 'Aden', css: 'hue-rotate(-20deg) contrast(0.9) saturate(0.85) brightness(1.2)' },
  { id: 'perpetua', label: 'Perpetua', css: 'brightness(1.05) saturate(1.1) contrast(1.05)' },
  { id: 'ludwig', label: 'Ludwig', css: 'contrast(1.05) saturate(0.9) sepia(0.12) brightness(1.02)' },
  { id: 'crema', label: 'Crema', css: 'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.6)' },
  { id: 'valencia', label: 'Valencia', css: 'sepia(0.15) saturate(1.5) contrast(0.9) brightness(1.1)' },
  { id: 'xpro', label: 'X-Pro', css: 'contrast(1.3) saturate(1.5) sepia(0.1) brightness(0.9)' },
  { id: 'noir', label: 'Noir', css: 'grayscale(1) contrast(1.3) brightness(0.9)' },
  { id: 'warm', label: 'Warm', css: 'sepia(0.3) saturate(1.4) brightness(1.05)' },
  { id: 'cool', label: 'Cool', css: 'hue-rotate(15deg) saturate(0.8) brightness(1.1) contrast(1.05)' },
  { id: 'fade', label: 'Fade', css: 'contrast(0.8) brightness(1.15) saturate(0.7)' },
];

export const applyFilterToCanvas = (imageSrc, filterCss) => {
  return new Promise((resolve) => {
    if (!filterCss || filterCss === 'none') { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.filter = filterCss;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    };
    img.onerror = () => resolve(null);
    img.src = imageSrc;
  });
};

const PhotoFilters = ({ mediaPreview, onFilterChange }) => {
  const [selectedFilter, setSelectedFilter] = useState('normal');
  const scrollRef = useRef(null);

  const handleSelect = useCallback((filter) => {
    setSelectedFilter(filter.id);
    onFilterChange(filter);
  }, [onFilterChange]);

  // Reset when media changes
  useEffect(() => {
    setSelectedFilter('normal');
    onFilterChange(FILTERS[0]);
  }, [mediaPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mediaPreview) return null;

  return (
    <div className="mt-3" data-testid="photo-filters">
      {/* Main preview with filter */}
      <div className="relative rounded-xl overflow-hidden mb-3 bg-gray-100">
        <img
          src={mediaPreview}
          alt="Preview"
          className="w-full max-h-64 object-contain"
          style={{ filter: selectedFilter !== 'normal' ? FILTERS.find(f => f.id === selectedFilter)?.css : 'none' }}
          data-testid="filtered-preview"
        />
        {selectedFilter !== 'normal' && (
          <span className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full font-medium backdrop-blur-sm">
            {FILTERS.find(f => f.id === selectedFilter)?.label}
          </span>
        )}
      </div>

      {/* Filter thumbnails — horizontal scroll */}
      <div ref={scrollRef} className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1">
        {FILTERS.map((filter) => (
          <button
            key={filter.id}
            onClick={() => handleSelect(filter)}
            data-testid={`filter-${filter.id}`}
            className={`flex-shrink-0 flex flex-col items-center space-y-1 transition-all ${
              selectedFilter === filter.id ? 'scale-105' : 'opacity-70 hover:opacity-100'
            }`}
          >
            <div className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
              selectedFilter === filter.id ? 'border-[#4A90E2] shadow-md shadow-[#4A90E2]/20' : 'border-transparent'
            }`}>
              <img
                src={mediaPreview}
                alt={filter.label}
                className="w-full h-full object-cover"
                style={{ filter: filter.css }}
              />
            </div>
            <span className={`text-[10px] font-medium ${
              selectedFilter === filter.id ? 'text-[#4A90E2]' : 'text-gray-500'
            }`}>
              {filter.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export { FILTERS };
export default PhotoFilters;
